"use client";

import React, { useEffect, useState } from "react";
import Image from "next/image";
import styles from "./EcomFeaturesShowcase.module.css";
import {
  DEFAULT_IMAGES,
  LEFT_ITEMS,
  RIGHT_ITEMS,
} from "../../data/ecommerce-development";

const BRAND = "#F48428";

// Interleave left/right for mobile rows: [L1, R1, L2, R2, L3, R3]
const ALL_ITEMS = LEFT_ITEMS.flatMap((l, i) =>
  [l, RIGHT_ITEMS[i]].filter(Boolean)
);

function FeaturePill({ icon, title }) {
  return (
    <div
      className="d-inline-flex align-items-center gap-2 px-3 py-2 rounded-3 mb-3 feature-pill"
      style={{
        background: "rgba(255,255,255,0.06)",
        border: "1px solid rgba(255,255,255,0.12)",
        color: "#fff",
        transition:
          "transform .25s cubic-bezier(.2,.7,.3,1), box-shadow .25s, border-color .25s, background .25s",
      }}
      data-reveal="slide-right"
    >
      <span
        className="d-inline-flex align-items-center justify-content-center"
        style={{
          width: 54,
          height: 54,
          borderRadius: 999,
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.03))",
          border: "1px solid rgba(255,255,255,0.18)",
          color: BRAND,
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          boxShadow: "0 6px 16px rgba(0,0,0,0.25)",
        }}
      >
        <i className={icon}></i>
      </span>
      <span className="fw-medium">{title}</span>
    </div>
  );
}

export default function EcomFeaturesShowcase({ images, mobileImage }) {
  const IMAGES =
    Array.isArray(images) && images.length > 0 ? images : DEFAULT_IMAGES;
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 576px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  // Center image: override on mobile if a specific mobileImage is provided
  const centerSrc = isMobile && mobileImage ? mobileImage : IMAGES[0];
  // Build a list of 5 images centered around centerSrc
  const wrap = (i) => ((i % IMAGES.length) + IMAGES.length) % IMAGES.length;
  const slides = [
    IMAGES[wrap(-2)],
    IMAGES[wrap(-1)],
    centerSrc,
    IMAGES[wrap(1)],
    IMAGES[wrap(2)],
  ];

  function posStyle(pos) {
    // pos -2, -1, 0, 1, 2
    const x =
      pos === 0
        ? 0
        : pos === -1
          ? -190
          : pos === 1
            ? 190
            : pos === -2
              ? -340
              : 340;
    const s = pos === 0 ? 0.96 : Math.abs(pos) === 1 ? 0.74 : 0.66;
    const o = pos === 0 ? 1 : Math.abs(pos) === 1 ? 0.88 : 0.6;
    const z = 100 - Math.abs(pos);
    return {
      transform: `translate(-50%, -50%) translateX(${x}px) scale(${s})`,
      zIndex: z,
      opacity: o,
    };
  }
  return (
    <section
      className={`py-5 position-relative ${styles.root}`}
      style={{
        background: "rgba(0,0,0,0)",
        overflowX: "hidden",
        overflowY: "hidden",
      }}
    >
      {/* Subtle neutral accents */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: "-20% -10% auto -10%",
          height: 420,
          background: `radial-gradient(600px 220px at 20% 30%, rgba(255,255,255,0.035), transparent 60%)`,
        }}
      />
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: "auto -10% -30% -10%",
          height: 420,
          background: `radial-gradient(600px 220px at 80% 70%, rgba(255,255,255,0.025), transparent 60%)`,
        }}
      />

      <div className="container">
        <div className="text-center mb-4">
          <div
            className="text-uppercase small"
            style={{ color: BRAND, letterSpacing: ".18em" }}
            data-reveal="fade"
          >
            Features
          </div>
          <h2 className="fw-bold text-white" data-reveal="slide-up">
            Unlock Competitive Advantage with Custom E‑commerce Solutions
          </h2>
          <p
            className="text-secondary m-0"
            style={{ maxWidth: 860, margin: "0 auto" }}
            data-reveal="fade"
          >
            Explore how our custom e‑commerce solutions can elevate your
            business to new heights.
          </p>
        </div>

        <div className="row g-4 align-items-center">
          {/* Left feature pills (desktop/tablet only) */}
          <div className="col-12 col-md-4 d-none d-md-flex flex-column align-items-md-end align-items-start order-md-1">
            <div className="pe-md-3" data-reveal="stagger-up">
              {LEFT_ITEMS.map((it) => (
                <FeaturePill key={it.title} icon={it.icon} title={it.title} />
              ))}
            </div>
          </div>

          {/* Center device mockup (single image) */}
          <div className="col-12 col-md-4 d-flex justify-content-center order-1 order-md-2">
            <div className="ecom-stack-shell" data-reveal="zoom-in">
              <div className="ecom-stack-rings" aria-hidden>
                <span />
                <span />
                <span />
              </div>
              <div className="ecom-stack-viewport">
                {slides.map((src, i) => {
                  const order = [-2, -1, 0, 1, 2][i];
                  const showNotch = order === 0;
                  return (
                    <div
                      className="ecom-stack-card"
                      key={`${src}-${i}`}
                      style={posStyle(order)}
                    >
                      <div className="ecom-stack-card-inner">
                        {showNotch && (
                          <span className="device-notch" aria-hidden></span>
                        )}
                        <div className="screen">
                          <Image
                            src={src}
                            alt="ecommerce preview"
                            fill
                            sizes="(min-width: 992px) 520px, 90vw"
                            priority={showNotch}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right feature pills (desktop/tablet only) */}
          <div className="col-12 col-md-4 d-none d-md-block order-md-3">
            <div className="ps-md-3 right-pills" data-reveal="stagger-up">
              {RIGHT_ITEMS.map((it) => (
                <FeaturePill key={it.title} icon={it.icon} title={it.title} />
              ))}
            </div>
          </div>

          {/* Mobile: same pill design in a compact two-column grid under the image */}
          <div className="col-12 d-md-none order-2 mobile-grid-col">
            <div className="mobile-pill-grid" data-reveal="stagger-up">
              {ALL_ITEMS.map((it) => (
                <div className="mobile-pill" key={it.title}>
                  <FeaturePill icon={it.icon} title={it.title} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
